import { useIntl } from 'react-intl'
import clsx from 'clsx'
import { rem } from 'polished'
import styled from 'styled-components'

import FormatCurrency from 'components/Common/FormatCurrency'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import IconButton from 'LuxKit/Button/IconButton'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineEllipsisVIcon from 'LuxKit/Icons/line/LineEllipsisVIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import Label from 'LuxKit/Label/Label'
import ListItemThumbnail from 'LuxKit/List/ListItemThumbnail'
import NestableClickableListItem from 'LuxKit/List/NestableClickableListItem'
import productLabels from 'i18n/productLabels'
import BookedLabel from 'tripPlanner/components/BookedLabel'
import type { BookedHotelSummary, HotelTilePrice } from '../hooks/useTripPlanHotelTileData'

const HotelTileContainer = styled(NestableClickableListItem)`
  border: 1px dashed ${props => props.theme.palette.neutral.surface.lightForeground};
  transition: border-color 180ms ease, background-color 200ms, color 200ms;

  &.has-saved-hotels {
    border-style: solid;
  }

  &:hover {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

const HotelThumbnail = styled(ListItemThumbnail)`
  width: ${rem(40)};
  height: ${rem(40)};

  svg {
    width: ${rem(16)};
    height: ${rem(16)};
  }
`

const BookedHotelImageWrapper = styled.div`
  width: ${rem(40)};
  height: ${rem(40)};
  overflow: hidden;
`

const HotelTileActions = styled(Group)`
  flex-shrink: 0;
`

const HotelBrowseButton = styled(TextButton)`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  white-space: nowrap;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.round};
  padding: ${rem(8)} ${rem(12)};
  line-height: 1;

  &:hover,
  &:focus-visible {
    border-color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }
`

interface Props {
  bookedHotelSummary?: BookedHotelSummary
  hasSavedHotels: boolean
  hotelTilePrice?: HotelTilePrice
  onActionsClick: () => void
  onOpen: () => void
  savedHotelCount: number
}

function TripPlanHotelTile({
  bookedHotelSummary,
  hasSavedHotels,
  hotelTilePrice,
  onActionsClick,
  onOpen,
  savedHotelCount,
}: Props) {
  const intl = useIntl()

  const hotelsLabel = intl.formatMessage(productLabels.hotels)

  const browseOffersLabel = intl.formatMessage({
    id: '6AkVNC1ApIHWK3b66-5b',
    defaultMessage: 'Browse offers',
    description: 'Button label to open the hotel offers/search drawer from the trip planner stop details',
  })

  const hotelTilePriceLabel = hotelTilePrice === undefined ? undefined : intl.formatMessage(
    {
      id: 'BW9zGX4U8wuVNhip8PV5',
      defaultMessage: 'From {price}/night',
      description: 'Per-night hotel price copy shown on the trip planner stop hotel tile',
    },
    { price: <FormatCurrency value={hotelTilePrice.price} currency={hotelTilePrice.currency} format="roundedDollar" /> },
  )

  const canOpenBookedHotelDetails = !!bookedHotelSummary?.offerId

  const hotelActionsLabel = canOpenBookedHotelDetails ? intl.formatMessage({
    id: 'er3KYQL--1nG7z4UFgVM',
    defaultMessage: 'View booked hotel details',
    description: 'Accessible label for the booked hotel summary action button that opens hotel details',
  }) : intl.formatMessage({
    id: '-kGlhMerE6iu-aaslfxi',
    defaultMessage: 'Browse hotel offers',
    description: 'Accessible label for the hotel empty state overflow action button that opens hotel offers',
  })

  const savedHotelsLabel = intl.formatMessage(
    {
      id: 'cUvlPRjyyculVBKmyRau',
      defaultMessage: '{count, plural, one {# saved} other {# saved}}',
      description: 'Status badge showing the number of saved hotels in a trip planner stop',
    },
    { count: savedHotelCount },
  )

  const bookedHotelNightsLabel = bookedHotelSummary?.nights === undefined ? undefined : intl.formatMessage(
    {
      id: 'LNKvL3HZkcGvajZC7thu',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Night count shown under a booked hotel in the trip planner stop hotel tile',
    },
    { count: bookedHotelSummary.nights },
  )

  return (
    <HotelTileContainer
      className={clsx({ 'has-saved-hotels': hasSavedHotels || !!bookedHotelSummary })}
      onClick={onOpen}
      size="medium"
      title={bookedHotelSummary?.name ?? hotelsLabel}
      titleWeight="bold"
      titleLineClamp={1}
      subtitle={bookedHotelSummary ? bookedHotelNightsLabel : hotelTilePriceLabel}
      subtitleLineClamp={1}
      startIcon={
        <HotelThumbnail kind={bookedHotelSummary ? 'neutral' : 'highlight-tertiary'} gutter="none">
          {bookedHotelSummary?.imageId && (
            <BookedHotelImageWrapper>
              <ResponsiveImage aspectRatio="1:1" id={bookedHotelSummary.imageId} fit="center" alt={bookedHotelSummary.name} />
            </BookedHotelImageWrapper>
          )}
          {!bookedHotelSummary?.imageId && (
            <LineBedIcon size="XS" />
          )}
        </HotelThumbnail>
      }
    >
      <HotelTileActions direction="horizontal" gap={8} verticalAlign="center">
        {bookedHotelSummary && <BookedLabel />}
        {!bookedHotelSummary && hasSavedHotels && (
          <Label kind="tertiary" variant="success">
            {savedHotelsLabel}
          </Label>
        )}
        {!bookedHotelSummary && !hasSavedHotels && (
          <HotelBrowseButton size="small" kind="tertiary" onClick={onOpen} startIcon={<LineSearchIcon size="XS" />}>
            {browseOffersLabel}
          </HotelBrowseButton>
        )}
        <IconButton
          size="small"
          kind="tertiary"
          onClick={onActionsClick}
          aria-label={hotelActionsLabel}
        >
          <LineEllipsisVIcon />
        </IconButton>
      </HotelTileActions>
    </HotelTileContainer>
  )
}

export default TripPlanHotelTile
